import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { videoAPI, appointmentsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Logo from '@/components/Logo';
import {
  Video,
  VideoOff,
  Mic,
  MicOff,
  Phone,
  PhoneOff,
  Maximize2,
  MessageSquare,
  Users,
  Clock,
  AlertCircle,
  Loader2,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const VideoRoom = () => {
  const { appointmentId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const jitsiContainerRef = useRef(null);
  const jitsiApiRef = useRef(null);
  const initializedRef = useRef(false); // Guard against double initialization
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [roomInfo, setRoomInfo] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const [callEnded, setCallEnded] = useState(false);
  const [callDuration, setCallDuration] = useState(0);

  useEffect(() => {
    // Guard against React StrictMode double-invocation
    if (initializedRef.current) return;
    initializedRef.current = true;
    
    initializeRoom();
    return () => {
      if (jitsiApiRef.current) {
        jitsiApiRef.current.dispose();
        jitsiApiRef.current = null;
      }
      initializedRef.current = false;
    };
  }, [appointmentId]);

  useEffect(() => {
    // Timer for call duration
    let interval;
    if (roomInfo && !callEnded) {
      interval = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [roomInfo, callEnded]);

  const initializeRoom = async () => {
    try {
      setLoading(true);
      
      // Get appointment details
      const appointmentRes = await appointmentsAPI.getDetail(appointmentId);
      setAppointment(appointmentRes.data.appointment);
      
      // Create or get video room
      const roomRes = await videoAPI.createRoom(appointmentId);
      setRoomInfo(roomRes.data);
      
      // Initialize Jitsi
      setTimeout(() => {
        initJitsi(roomRes.data);
      }, 500);
      
    } catch (err) {
      console.error('Error initializing room:', err);
      setError(err.response?.data?.detail || 'Erreur lors de la création de la salle');
      toast.error('Impossible de rejoindre l\'appel vidéo');
    } finally {
      setLoading(false);
    }
  };

  const initJitsi = (room) => {
    if (!jitsiContainerRef.current) return;
    
    // Load Jitsi external API script if not already loaded
    if (!window.JitsiMeetExternalAPI) {
      const script = document.createElement('script');
      script.src = 'https://meet.jit.si/external_api.js';
      script.async = true;
      script.onload = () => createJitsiMeeting(room);
      document.body.appendChild(script);
    } else {
      createJitsiMeeting(room);
    }
  };

  const createJitsiMeeting = (room) => {
    // Dispose previous instance if exists to prevent duplicates
    if (jitsiApiRef.current) {
      jitsiApiRef.current.dispose();
      jitsiApiRef.current = null;
    }
    
    if (!jitsiContainerRef.current) return;
    
    const domain = 'meet.jit.si';
    const options = {
      roomName: room.room_name,
      width: '100%',
      height: '100%',
      parentNode: jitsiContainerRef.current,
      userInfo: {
        displayName: user?.role === 'professional' ? `Dr. ${user?.full_name}` : user?.full_name,
        email: user?.email,
      },
      configOverwrite: {
        startWithAudioMuted: false,
        startWithVideoMuted: false,
        prejoinPageEnabled: false,
        disableDeepLinking: true,
        defaultLanguage: 'fr',
        toolbarButtons: [
          'microphone',
          'camera',
          'desktop',
          'fullscreen',
          'chat',
          'settings',
          'hangup',
        ],
      },
      interfaceConfigOverwrite: {
        SHOW_JITSI_WATERMARK: false,
        SHOW_WATERMARK_FOR_GUESTS: false,
        SHOW_BRAND_WATERMARK: false,
        BRAND_WATERMARK_LINK: '',
        SHOW_POWERED_BY: false,
        TOOLBAR_ALWAYS_VISIBLE: true,
        DEFAULT_BACKGROUND: '#1A237E',
        DISABLE_PRESENCE_STATUS: true,
        DISABLE_JOIN_LEAVE_NOTIFICATIONS: false,
        MOBILE_APP_PROMO: false,
      },
    };

    jitsiApiRef.current = new window.JitsiMeetExternalAPI(domain, options);

    // Event listeners
    jitsiApiRef.current.addListener('readyToClose', handleCallEnd);
    jitsiApiRef.current.addListener('participantLeft', handleParticipantLeft);
    jitsiApiRef.current.addListener('videoConferenceLeft', handleCallEnd);
  };

  const handleCallEnd = async () => {
    setCallEnded(true);
    
    if (user?.role === 'professional') {
      try {
        await videoAPI.endCall(appointmentId);
        toast.success('Consultation terminée');
      } catch (err) {
        console.error('Error ending call:', err);
      }
    }
  };

  const handleParticipantLeft = (participant) => {
    toast.info(`${participant.displayName || 'Un participant'} a quitté l'appel`);
  };

  const handleEndCall = async () => {
    if (jitsiApiRef.current) {
      jitsiApiRef.current.executeCommand('hangup');
    }
    await handleCallEnd();
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A237E] flex items-center justify-center">
        <div className="text-center text-white">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Connexion à la consultation...</h2>
          <p className="text-white/70">Veuillez patienter</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-[#1A237E] mb-2">
              Impossible de rejoindre l'appel
            </h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <Button
              onClick={() => navigate(-1)}
              className="bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-xl"
            >
              Retour
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (callEnded) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-500" />
            </div>
            <h2 className="text-xl font-bold text-[#1A237E] mb-2">
              Consultation terminée
            </h2>
            <p className="text-slate-600 mb-2">
              Durée : {formatDuration(callDuration)}
            </p>
            {user?.role === 'patient' && (
              <p className="text-sm text-slate-500 mb-6">
                N'oubliez pas de laisser un avis sur votre consultation !
              </p>
            )}
            <div className="flex gap-3 justify-center">
              {user?.role === 'patient' && (
                <Button
                  onClick={() => navigate('/appointments')}
                  className="bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-xl"
                >
                  Laisser un avis
                </Button>
              )}
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="rounded-xl"
              >
                Retour au tableau de bord
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1A237E] flex flex-col">
      {/* Header */}
      <header className="bg-[#1A237E]/90 backdrop-blur-sm border-b border-white/10 px-4 py-3 flex items-center justify-between z-10">
        <div className="flex items-center gap-4">
          <Logo size="xs" showText={false} variant="icon" />
          <div className="text-white">
            <h1 className="font-semibold">Téléconsultation</h1>
            <p className="text-sm text-white/70">
              {user?.role === 'patient' 
                ? `Dr. ${appointment?.professional_name}`
                : appointment?.patient_name
              }
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-white bg-white/10 px-3 py-1.5 rounded-lg">
            <Clock className="w-4 h-4" />
            <span className="font-mono">{formatDuration(callDuration)}</span>
          </div>
          
          {user?.role === 'professional' && (
            <Button
              onClick={handleEndCall}
              className="bg-red-500 hover:bg-red-600 text-white rounded-xl gap-2"
              data-testid="end-call-btn"
            >
              <PhoneOff className="w-4 h-4" />
              Terminer
            </Button>
          )}
        </div>
      </header>

      {/* Jitsi Container */}
      <div 
        ref={jitsiContainerRef} 
        className="flex-1"
        data-testid="jitsi-container"
      />
    </div>
  );
};

export default VideoRoom;
